import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Download, Search } from "lucide-react";
import GastosMayor from "@/pages/GastosMayor";
import CuadreWallet from "@/pages/CuadreWallet";
import { dateInputForZone, monthInputForZone } from "@/lib/preferences";

const REPORT_LIMIT = 10000;

function ReportsTab() {
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const currentMonth = monthInputForZone(today);
    return {
      market: "todos",
      operation: "todas",
      status: "activa",
      currency: "todos",
      start_date: `${currentMonth}-01`,
      end_date: dateInputForZone(today),
      created_by: "todos",
      client_id: "todos",
    };
  });
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);

  const buildParams = () => {
    const p = { limit: REPORT_LIMIT };
    if (filters.market !== "todos") p.market = filters.market;
    if (filters.operation !== "todas") p.operation = filters.operation;
    if (filters.status !== "todas") p.status = filters.status;
    if (filters.currency !== "todos") p.currency = filters.currency;
    if (filters.start_date) p.start_date = filters.start_date;
    if (filters.end_date) p.end_date = filters.end_date;
    if (filters.created_by !== "todos") p.created_by = filters.created_by;
    if (filters.client_id !== "todos") p.client_id = filters.client_id;
    return p;
  };

  useEffect(() => {
    Promise.all([
      api.get("/users").catch(() => ({ data: [] })),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/currencies").catch(() => ({ data: [] })),
    ]).then(([u, c, cur]) => {
      setUsers(Array.isArray(u.data) ? u.data : []);
      setClients(Array.isArray(c.data) ? c.data : []);
      setCurrencies(Array.isArray(cur.data) ? cur.data : []);
    });
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/transactions", { params: buildParams() });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = async () => {
    const params = new URLSearchParams(buildParams()).toString();
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API}/reports/export?${params}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reporte_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = rows.reduce(
    (acc, r) => {
      if (r.operation_type === "compra") {
        acc.bought += r.usdt_amount;
        acc.invested += r.total_amount;
      } else {
        acc.sold += r.usdt_amount;
        acc.received += r.total_amount;
      }
      return acc;
    },
    { bought: 0, sold: 0, invested: 0, received: 0 }
  );

  // Métricas derivadas para el rango filtrado
  const isMayorOnly = filters.market === "mayor";
  const isDetalleOnly = filters.market === "detalle";
  const saldoRestante = totals.bought - totals.sold;
  const avgBuyUnit = totals.bought > 0 ? totals.invested / totals.bought : 0;
  const avgSellUnit = totals.sold > 0 ? totals.received / totals.sold : 0;
  // Mayor: porcentajes y P/L bruto en USD
  const avgBuyPct = totals.bought > 0 ? (totals.invested / totals.bought - 1) * 100 : 0;
  const avgSellPct = totals.sold > 0 ? (totals.received / totals.sold - 1) * 100 : 0;
  const benefitAvgPct = avgSellPct - avgBuyPct;
  const costOfSold = totals.sold * avgBuyUnit;
  const grossPLMayor = totals.received - costOfSold;
  // Detalle: P/L en DOP
  const benefitPerUsdt = avgSellUnit - avgBuyUnit;
  const profitLossDetalle = totals.sold * benefitPerUsdt;

  // Por defecto mostramos métricas estilo Mayor (% y USD); si el filtro es Detalle, mostramos DOP.
  const showDetalle = isDetalleOnly;
  const benefitLabel = "Beneficio promedio por USDT";
  const benefitValue = showDetalle ? `${fmt.dop(benefitPerUsdt)} DOP` : fmt.pct(benefitAvgPct);
  const plValue = showDetalle ? `${fmt.dop(profitLossDetalle)} DOP` : `${fmt.usdt(grossPLMayor)} USD`;
  const plNumber = showDetalle ? profitLossDetalle : grossPLMayor;

  return (
    <div className="space-y-6" data-testid="reportes-page">
      <div>
        <p className="text-slate-500 mt-1">Filtra y exporta operaciones</p>
      </div>

      <Card className="p-5 border border-slate-200 rounded-xl bg-white shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Mercado</Label>
            <Select value={filters.market} onValueChange={(v) => setFilters({ ...filters, market: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-market"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="detalle">Detalle</SelectItem>
                <SelectItem value="mayor">Mayor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Operación</Label>
            <Select value={filters.operation} onValueChange={(v) => setFilters({ ...filters, operation: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-op"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="compra">Compras</SelectItem>
                <SelectItem value="venta">Ventas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Estado</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="activa">Activas</SelectItem>
                <SelectItem value="anulada">Anuladas</SelectItem>
                <SelectItem value="todas">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Desde</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="mt-2" data-testid="rep-start" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Hasta</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="mt-2" data-testid="rep-end" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Operador</Label>
            <Select value={filters.created_by} onValueChange={(v) => setFilters({ ...filters, created_by: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-user"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Moneda</Label>
            <Select value={filters.currency} onValueChange={(v) => setFilters({ ...filters, currency: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas</SelectItem>
                {currencies.map((c) => (
                  <SelectItem key={c.id || c.symbol} value={c.symbol}>{c.symbol}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold text-slate-500">Cliente</Label>
            <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v })}>
              <SelectTrigger className="mt-2" data-testid="rep-filter-client"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2 mt-4 justify-end">
          <Button onClick={search} disabled={loading} className="bg-slate-900 hover:bg-slate-800" data-testid="rep-search">
            <Search size={16} className="mr-2" /> Buscar
          </Button>
          <Button onClick={exportCsv} variant="outline" data-testid="rep-export">
            <Download size={16} className="mr-2" /> Exportar CSV
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm" data-testid="rep-total-bought">
          <div className="text-xs uppercase font-semibold text-slate-500">Comprado</div>
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900 mt-1">{fmt.usdt(totals.bought)} <span className="text-xs text-slate-500">USDT</span></div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm" data-testid="rep-total-sold">
          <div className="text-xs uppercase font-semibold text-slate-500">Vendido</div>
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900 mt-1">{fmt.usdt(totals.sold)} <span className="text-xs text-slate-500">USDT</span></div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-500">Invertido</div>
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900 mt-1">{fmt.usdt(totals.invested)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm">
          <div className="text-xs uppercase font-semibold text-slate-500">Recibido</div>
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900 mt-1">{fmt.usdt(totals.received)}</div>
        </Card>
        <Card className="p-4 border border-slate-200 rounded-xl bg-white shadow-sm" data-testid="rep-total-transactions">
          <div className="text-xs uppercase font-semibold text-slate-500">Transacciones</div>
          <div className="font-mono tabular-nums text-xl font-bold text-slate-900 mt-1">{rows.length.toLocaleString("en-US")}</div>
          <div className="text-xs text-slate-400 mt-1">en el rango filtrado</div>
        </Card>
      </div>

      <Card className="p-5 border border-slate-200 rounded-xl bg-white shadow-sm" data-testid="rep-derived-metrics">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-slate-900 rounded-full" />
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">
            Resumen del rango filtrado
          </h3>
          <span className="text-xs text-slate-400">
            ({showDetalle ? "Mercado Detalle / DOP" : isMayorOnly ? "Mercado Mayor / USD" : "Mixto — mostrado en % y USD"})
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/50" data-testid="rep-saldo-restante">
            <div className="text-xs uppercase font-semibold text-slate-500">Saldo restante en USDT</div>
            <div className="font-mono tabular-nums text-2xl font-bold text-slate-900 mt-2">
              {fmt.usdt(saldoRestante)} <span className="text-xs text-slate-500">USDT</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {fmt.usdt(totals.bought)} comprado − {fmt.usdt(totals.sold)} vendido
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/50" data-testid="rep-beneficio-promedio">
            <div className="text-xs uppercase font-semibold text-slate-500">{benefitLabel}</div>
            <div className={`font-mono tabular-nums text-2xl font-bold mt-2 ${
              (showDetalle ? benefitPerUsdt : benefitAvgPct) >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}>
              {(showDetalle ? benefitPerUsdt : benefitAvgPct) > 0 ? "+" : ""}{benefitValue}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {showDetalle
                ? `Promedio venta ${fmt.dop(avgSellUnit)} − compra ${fmt.dop(avgBuyUnit)}`
                : `Venta % ${fmt.pct(avgSellPct)} − Compra % ${fmt.pct(avgBuyPct)}`}
            </div>
          </div>

          <div className="border border-slate-100 rounded-lg p-4 bg-slate-50/50" data-testid="rep-pl-total">
            <div className="text-xs uppercase font-semibold text-slate-500">P / G total aproximada</div>
            <div className={`font-mono tabular-nums text-2xl font-bold mt-2 ${
              plNumber >= 0 ? "text-emerald-600" : "text-rose-600"
            }`}>
              {plNumber > 0 ? "+" : ""}{plValue}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {showDetalle
                ? `${fmt.usdt(totals.sold)} USDT × ${fmt.dop(benefitPerUsdt)} DOP`
                : `Recibido ${fmt.usdt(totals.received)} − Costo ${fmt.usdt(costOfSold)}`}
            </div>
          </div>
        </div>
      </Card>

      <Card className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rep-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Mercado</th>
                <th className="px-4 py-3 text-left font-semibold">Op</th>
                <th className="px-4 py-3 text-left font-semibold">Moneda</th>
                <th className="px-4 py-3 text-right font-semibold">USDT</th>
                <th className="px-4 py-3 text-right font-semibold">Precio/%</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 text-left font-semibold">Cliente</th>
                <th className="px-4 py-3 text-left font-semibold">Operador</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
              </tr>
            </thead>
            <tbody className="font-mono tabular-nums">
              {rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Pulsa "Buscar" para cargar resultados</td></tr>
              )}
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-600">{fmt.date(t.transaction_date)}</td>
                  <td className="px-4 py-2 capitalize">{t.market_type}</td>
                  <td className="px-4 py-2 capitalize">{t.operation_type}</td>
                  <td className="px-4 py-2 font-semibold">{t.currency}</td>
                  <td className="px-4 py-2 text-right">{fmt.usdt(t.usdt_amount)}</td>
                  <td className="px-4 py-2 text-right">{t.market_type === "detalle" ? fmt.dop(t.rate_dop) : fmt.pct(t.percentage)}</td>
                  <td className="px-4 py-2 text-right">{fmt.usdt(t.total_amount)} {t.total_currency}</td>
                  <td className="px-4 py-2 font-sans text-xs">{t.client_name || "-"}</td>
                  <td className="px-4 py-2 font-sans text-xs">{t.created_by_name || "-"}</td>
                  <td className="px-4 py-2 capitalize">{t.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

export default function Reportes() {
  return (
    <div className="space-y-6" data-testid="reportes-container">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Reportes</h1>
        <p className="text-slate-500 mt-1">Operaciones, capital del mes y cuadre wallet/caja</p>
      </div>
      <Tabs defaultValue="reportes">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="reportes" data-testid="tab-rep-reportes">Reportes</TabsTrigger>
          <TabsTrigger value="gastos" data-testid="tab-rep-gastos">Gastos &amp; Capital Mayor</TabsTrigger>
          <TabsTrigger value="cuadre" data-testid="tab-rep-cuadre">Cuadre Wallet / Caja</TabsTrigger>
        </TabsList>
        <TabsContent value="reportes" className="mt-4">
          <ReportsTab />
        </TabsContent>
        <TabsContent value="gastos" className="mt-4">
          <GastosMayor embedded />
        </TabsContent>
        <TabsContent value="cuadre" className="mt-4">
          <CuadreWallet embedded />
        </TabsContent>
      </Tabs>
    </div>
  );
}
